<script setup>
import { ref, computed, watch, defineEmits, defineProps, defineExpose } from "vue";
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  TransitionRoot,
} from "@headlessui/vue";

import { debounce } from '@/utils/debounce';

// Debounced version (300ms delay)
const debouncedLoadOptions = debounce((q) => {
  if (props.loadOptions && q !== "") {
    if (props.createOption)
      options.value = [];
    isLoading.value = true;
    props.loadOptions(q, results => {
      options.value = results;

      // if (props.modelValue && !options.value.some(o => 
      //   o[props.idField] === props.modelValue?.[props.idField])
      // ) {
      //   options.value.unshift(props.modelValue);
      // }

      isLoading.value = false;
    });
  }
}, 300);

const emit = defineEmits(["update:modelValue", "add-item", "change"]);

const props = defineProps({
  modelValue: Object,
  placeHolder: String,
  options: {
    type: Array,
    default: () => [],
  },
  loadOptions: Function,
  createOption: Function,
  idField: {  // New prop to specify the ID field name (defaults to 'value')
    type: String,
    default: 'value'
  }
});

const options = ref(props.options);
const isLoading = ref(false);
const comboboxButton = ref(null);
const comboboxInput = ref(null)
// Expose methods to parent component
defineExpose({
  setSelected,
  addOption
});

const queryOption = computed(() => {
  return query.value === ""
    ? null
    : {
      missing: true,
      label: query.value,
    };
});

let query = ref("");
watch(
  query,
  q => {
    debouncedLoadOptions(q); // ← Now debounced!
  },
  { immediate: true }
);

watch(() => props.options, (newValue) => {
  options.value = newValue;
}, { immediate: true })

let filteredOptions = computed(() => {
  // Null check for options.value and query.value
  if (!options.value || !Array.isArray(options.value)) return [];
  if (!query.value) return options.value;

  const cleanQuery = String(query.value)
    .toLowerCase()
    .replace(/\s+/g, "");

  return options.value.filter(option => {
    // Full null check chain
    const optionLabel = option?.label;
    if (!optionLabel) return false;

    return String(optionLabel)
      .toLowerCase()
      .replace(/\s+/g, "")
      .includes(cleanQuery);
  });
});

function addOption(option) {
  const stringValue = String(option.value);
  const existingIndex = options.value.findIndex(o => String(o.value) === stringValue);

  if (existingIndex === -1) {
    // Option doesn't exist - add it to the beginning
    options.value.unshift(option);
  } else {
    // Option exists - update it while preserving its position
    options.value[existingIndex] = {
      ...options.value[existingIndex], // keep existing properties
      ...option                       // apply new properties
    };
  }
}
// Programmatically set selected value
function setSelected(option) {
  emit("update:modelValue", option);
  emit("change", option);
}

function handleUpdateModelValue(selected) {
  if (!selected?.missing) {
    emit("update:modelValue", selected);
    emit("change", selected);
  }

  if (props.createOption && selected?.missing) {
    isLoading.value = true;
    props.createOption(selected, option => {
      const newOption = {
        ...option,
        // Ensure the ID field matches the expected prop
        [props.idField]: option[props.idField] || option.value || option.sysfkid
      };
      addOption(newOption);
      setSelected(newOption);
      emit("add-item", newOption);
      isLoading.value = false;
    });
  }
}

function handleClick() {
  if (comboboxButton.value?.$el) {
    if (comboboxInput.value?.$el) {
      comboboxInput.value.$el.select();
    }
    comboboxButton.value.$el.click();
  }
}
</script>

<template>
  <Combobox by="value" :model-value="props.modelValue" @update:model-value="handleUpdateModelValue">
    <div class="combobox-container">
      <div class="input-group has-trailing-icon">
        <ComboboxInput class="form-control form-control-sm" ref="comboboxInput" autocomplete="off"
          :displayValue="option => option.label" @change="query = $event.target.value" :placeholder="props.placeHolder"
          @click="handleClick" />
        <ComboboxButton ref="comboboxButton" class="btn btn-icon">
          <i class="bi bi-chevron-down chevron-icon"></i>
        </ComboboxButton>
      </div>

      <TransitionRoot leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0"
        @after-leave="query = ''">
        <ComboboxOptions class="dropdown-options">
          <div v-if="filteredOptions.length === 0 && !isLoading && !queryOption && !props.createOption"
            class="empty-state">
            <span v-if="query !== ''"><i class="bi bi-search me-2"></i> No results found</span>
            <span v-else><i class="bi bi-search me-2"></i> Enter keyword to start search</span>
          </div>

          <!-- <div v-if="isLoading" class="loading-state">
            <div class="spinner-border spinner-border-sm me-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            Loading options...
          </div> -->

          <template v-if="!isLoading">
            <ComboboxOption v-if="queryOption && !filteredOptions.length && props.createOption" as="template"
              :value="queryOption" v-slot="{ active }">
              <li class="option-item create-option" :class="{ 'active-option': active }">
                <i class="bi bi-plus-circle-fill me-2"></i>
                Create "{{ queryOption.label }}"
              </li>
            </ComboboxOption>

            <ComboboxOption v-for="option in filteredOptions" as="template" :key="option.value" :value="option"
              v-slot="{ selected, active }">
              <li class="option-item" :class="{ 'active-option': active }">
                <span class="checkmark"
                  v-if="selected && option.value !== 0 && option.value !== '0' && option.value !== null && option.value !== ''">
                  <i class="bi bi-check2 h-5 w-5"></i>
                </span>
                <span class="option-label">
                  {{ option.label }}
                </span>

              </li>
            </ComboboxOption>
          </template>
        </ComboboxOptions>
      </TransitionRoot>
    </div>
  </Combobox>
</template>

<style scoped>
.combobox-container {
  position: relative;
}

.input-group.has-trailing-icon {
  position: relative;
  align-items: stretch;
}

.btn.btn-icon {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #6c757d;
  z-index: 22;
  cursor: pointer;
}

.btn.btn-icon:hover {
  color: #495057;
}

.chevron-icon {
  transition: transform 0.2s ease;
  font-size: 1rem;
}

.combobox-button[aria-expanded="true"] .chevron-icon {
  transform: rotate(180deg);
}

.form-control {
  padding-right: 2.5rem !important;
  border-radius: 0.5rem !important;
}

.dropdown-options {
  position: absolute;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  margin-top: 0.25rem;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  z-index: 9999;
}

.option-item {
  padding: 0.75rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #f1f1f1;
  transition: all 0.2s ease;
}

.option-item:last-child {
  border-bottom: none;
}

.option-item:hover {
  background-color: #f8f9fa;
}

.active-option {
  background-color: #0d6efd !important;
  color: white;
}

.checkmark {
  margin-right: 0.75rem;
  display: inline-flex;
  color: #0d6efd;
}

.active-option .checkmark {
  color: white;
}

.option-label {
  flex-grow: 1;
}

.empty-state,
.loading-state {
  padding: 1rem;
  text-align: center;
  color: #6c757d;
}

.create-option {
  color: #0d6efd;
  font-weight: 500;
}

.active-option.create-option {
  color: white;
}

.input-group .form-control:focus {
  box-shadow: none;
  border-color: #86b7fe;
  outline: 0;
}
</style>